<template>
  <section
    class="home-page"
    v-if="!loading"
  >
    <template v-if="doc.body">
      <header class="home-page__header">
        <h1
          class="home-page__header__title"
          v-html="doc.title"
        />

        <div class="home-page__header__version">
          <div class="home-page__header__version__label">
            {{ $t('version') }}: <em v-html="$route.params.version" />
          </div>

          <div class="home-page__header__version__last-updated">
            {{ $t('lastUpdated') }}: <em>{{ latestVersion }}</em>
          </div>
        </div>
      </header>

      <div
        class="home-page__body"
        v-html="doc.body"
      />
    </template>

    <div
      class="home-page__empty"
      v-else
    >
      <div class="home-page__empty__emoji">
        (&gt;_&lt;)
      </div>

      <p v-html="$t('emptyState')" />
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import marked from 'marked'
import DOMPurify from 'dompurify'

export default {
  name: 'DocumentViewer',

  data () {
    return {
      doc: {
        title: '',
        lastUpdated: '',
        body: ''
      }
    }
  },

  computed: {
    ...mapState('global', ['loading', 'latestVersion'])
  },

  methods: {
    ...mapMutations('global', [
      'SET_LOADING',
      'SET_CENTERED'
    ]),

    docClear () {
      const doc = {
        title: '',
        lastUpdated: '',
        body: ''
      }

      Object.assign(this.doc, doc)
    },

    docFetch (langFallback) {
      const api = process.env.VUE_APP_API
      let { lang, doc, version } = this.$route.params

      if (langFallback) {
        lang = langFallback
      }

      const url = `${api}/${version}/${lang}/${doc}.md`

      this.docClear()
      this.SET_LOADING(true)

      axios.get(url)
        .then(response => {
          this.docFormat(response.data)
          this.SET_CENTERED(false)
        })
        .catch(() => {
          const langDefault = process.env.VUE_APP_I18N_LOCALE

          if (lang !== langDefault) {
            return this.docFetch(langDefault)
          }

          this.docClear()
          this.SET_CENTERED(true)
        })
        .then(() => {
          this.SET_LOADING(false)
        })
    },

    docFormat (data) {
      const markedOptions = {
        headerIds: false
      }
      const html = marked(data, markedOptions)
      const htmlPruned = DOMPurify.sanitize(html).split('\n')
      const title = htmlPruned.shift().replace(/^<h1>(.+)<\/h1>$/, '$1')
      const lastUpdated = htmlPruned.shift().replace(/^<h2>(.+)<\/h2>$/, '$1')
      const body = htmlPruned.join('')
      const doc = {
        body,
        title,
        lastUpdated
      }

      Object.assign(this.doc, doc)
    }
  },

  mounted () {
    this.docFetch()
  },

  watch: {
    $route () {
      this.docFetch()
    }
  }
}
</script>

<style lang="scss">
.home-page {
  &__header {
    padding: 40px 0 20px;
    text-align: center;

    &__title {
      font: {
        weight: 900;
        size: 3.1rem;
      }
      line-height: 110%;
      text-transform: uppercase;
    }

    &__version {
      display: inline-flex;
      justify-content: flex-start;
      align-items: center;
      margin-top: 30px;
      font-size: 0.9rem;
      line-height: 140%;

      &__last-updated {
        padding-left: 20px;
        margin-left: 20px;
        border-left: 1px solid;
      }
    }
  }

  &__body {
    padding-bottom: 60px;
    text-align: justify;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 1.5em 0;
      font-weight: 900;
      line-height: 120%;
      text-transform: uppercase;
    }

    h1 {
      font-size: 2.1rem;
    }

    h2 {
      font-size: 1.8rem;
    }

    h3 {
      font-size: 1.5rem;
    }

    h4 {
      font-size: 1.3rem;
    }

    h5 {
      font-size: 1.2rem;
    }

    h6 {
      font-size: 1.1rem;
    }

    p {
      margin: 1.5em 0;
      font-size: 1rem;
      line-height: 140%;
    }

    ul,
    ol {
      margin: 1.5em 0;
      padding-inline-start: 20px;

      li {
        margin: 0.5em 0;
        list-style-position: outside;
        font-size: 1rem;
        line-height: 140%;
      }
    }

    ol {
      li {
        list-style-type: decimal;
      }
    }

    ul {
      li {
        list-style-type: disc;
      }
    }

    table {
      width: 100%;
      margin: 2em 0;
      border-collapse: collapse;

      th,
      td {
        padding: 15px;
        line-height: 140%;
        text-align: left;
      }

      th {
        font: {
          weight: 700;
          size: 0.9rem;
        }
        text: {
          transform: uppercase;
          text-align: left;
        }
      }

      td {
        border-top: 1px solid map-get($color-util, 'muted');
        font-size: 1rem;
      }
    }
  }

  &__empty {
    padding: 60px 0;
    text-align: center;

    &__emoji {
      margin-bottom: 40px;
      font-size: 6rem;
    }

    p {
      font-size: 1.3rem;
    }
  }

  // Responsiveness
  @include media(map-get($breakpoint, 'medium')) {
    &__header {
      &__title {
        font-size: 1.9rem;
      }

      &__version {
        flex-direction: column;
        margin-top: 15px;
        font-size: 0.8rem;

        &__last-updated {
          padding-left: 0;
          margin-left: 0;
          border: 0;
        }
      }
    }

    &__body {
      padding-bottom: 40px;

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: 1em 0;
        line-height: 110%;
      }

      h1 {
        font-size: 1.8rem;
      }

      h2 {
        font-size: 1.6rem;
      }

      h3 {
        font-size: 1.3rem;
      }

      h4 {
        font-size: 1.1rem;
      }

      h5 {
        font-size: 1rem;
      }

      h6 {
        font-size: 0.9rem;
      }

      p {
        margin: 1.3em 0;
        font-size: 0.9rem;
      }

      ul,
      ol {
        margin: 1.3em 0;

        li {
          margin: 0.5em 0;
          font-size: 0.9rem;
        }
      }

      table {
        tr {
          display: block;
          margin-top: 1.5em;
          border: 3px solid map-get($color-util, 'muted');

          &:only-child {
            margin: 0;
          }
        }

        th,
        td {
          display: block;
          padding: 10px;

          &:first-child {
            border: 0;
          }
        }

        th {
          font-size: 0.8rem;
        }

        td {
          font-size: 0.9rem;
        }
      }
    }

    &__empty {
      padding: 30px 0;

      &__emoji {
        margin-bottom: 30px;
        font-size: 4rem;
      }

      p {
        font-size: 1rem;
      }
    }
  }
}
</style>
